import { useEffect, useState, useCallback } from 'react';
import {
    getTournamentsWithOrganizers,
    getRegularTournamentsWithOrganizers,
    generateRegularTournamentEvents
} from '../firebase/firestore';

// Funkcja do obliczania odległości między dwoma punktami (wzór Haversine)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Promień Ziemi w kilometrach
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Odległość w kilometrach
    return distance;
};

// Funkcja do rozszerzania turniejów wielodniowych
const expandMultiDayTournaments = (tournaments) => {
    const expandedTournaments = [];
    
    tournaments.forEach(tournament => {
        // Domyślnie 1 dzień dla starych rekordów bez pola duration
        const duration = tournament.duration || 1;
        
        // Dodaj oryginalny turniej
        expandedTournaments.push(tournament);
        
        // Jeśli turniej trwa więcej niż 1 dzień, dodaj duplikaty dla kolejnych dni
        if (duration > 1) {
            const startDate = new Date(tournament.date);
            
            for (let i = 1; i < duration; i++) {
                const nextDate = new Date(startDate);
                nextDate.setDate(startDate.getDate() + i);
                
                const tournamentCopy = {
                    ...tournament,
                    id: `${tournament.id}_day_${i + 1}`,
                    date: nextDate.toISOString().split('T')[0], // Format YYYY-MM-DD
                    isMultiDayCopy: true,
                    originalTournamentId: tournament.id,
                    dayNumber: i + 1
                };
                
                expandedTournaments.push(tournamentCopy);
            }
        }
    });
    
    return expandedTournaments;
};

// Hook do pobierania danych turniejów z Firebase
export const useTournaments = (filters = {}, currentMonth = null) => {
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTournaments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Pobierz zwykłe turnieje z organizatorami
            let data = await getTournamentsWithOrganizers();

            // Pobierz turnieje regularne z organizatorami
            const regularTournaments = await getRegularTournamentsWithOrganizers();
            console.log('Pobrane turnieje regularne:', regularTournaments);

            // Generuj wydarzenia z turniejów regularnych dla określonego miesiąca lub bieżącego
            const targetDate = currentMonth || new Date();
            console.log('Generowanie wydarzeń dla miesiąca:', targetDate.getFullYear(), targetDate.getMonth() + 1);
            const regularEvents = generateRegularTournamentEvents(
                regularTournaments, 
                targetDate.getFullYear(), 
                targetDate.getMonth()
            );
            console.log('Wygenerowane wydarzenia z turniejów regularnych:', regularEvents);

            // Połącz zwykłe turnieje z wydarzeniami z turniejów regularnych
            data = [...data, ...regularEvents];

            // Jeśli filtrujemy po rangach, zastosuj filtrowanie po pobraniu
            if (filters.ranks && filters.ranks.length > 0) {
                data = data.filter(tournament => filters.ranks.includes(tournament.rank));
            }

            // Rozszerz turnieje wielodniowe
            const expandedData = expandMultiDayTournaments(data);

            // Filtruj po lokalizacji jeśli jest włączone
            if (filters.useLocation && filters.location && filters.radius) {
                const filteredData = expandedData.filter(tournament => {
                    // Nie filtruj po lokalizacji dla ważniejszych rang - zawsze je pokazuj
                    if (['NATIONAL', 'INTERNATIONAL', 'FOREIGN'].includes(tournament.rank)) {
                        return true;
                    }

                    // Filtruj po lokalizacji tylko dla LOCAL i REGIONAL
                    if (['LOCAL', 'REGIONAL'].includes(tournament.rank)) {
                        const tournamentLocation = tournament.customLocation?.coordinates || tournament.organizer?.coordinates;
                        if (!tournamentLocation) return false;

                        const distance = calculateDistance(
                            filters.location.lat,
                            filters.location.lng,
                            tournamentLocation.lat,
                            tournamentLocation.lng
                        );

                        return distance <= filters.radius;
                    }

                    // Dla innych rang (jeśli jakieś są) nie filtruj po lokalizacji
                    return true;
                });
                setTournaments(filteredData);
            } else {
                setTournaments(expandedData);
            }
        } catch (err) {
            setError(err.message);
            console.error('Błąd podczas pobierania turniejów:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, currentMonth]);

    useEffect(() => {
        fetchTournaments();
    }, [fetchTournaments]);

    return { tournaments, loading, error, refetch: fetchTournaments };
};